import { useMemo, type VoidFunctionComponent } from 'react';

import { Action } from '../../../../../typings/permissions';
import { useLocation } from '../../../../contexts/RouterContext';
import {
  userHasPermissionForReleaseGroup,
  userHasPermissionToSetReleaseGroupPolicy,
} from '../../../../helpers/permissions';
import { useSelectedReleaseId } from '../../../../redux/project_grouping/hooks';
import { PolicyType } from '../../../../types';
import Subnav from '../../../generic/Subnav';
import ReleaseGroupSubnavItemInventory from '../../inventory/ReleaseGroupSubnavItemInventory';
import type { InventoryScope } from '../../inventory/types';

import ReleaseGroupSubnavItemIssues from './ReleaseGroupSubnavItemIssues';
import ReleaseGroupSubnavItemReports from './ReleaseGroupSubnavItemReports';

/**
 * Hook which looks at location.pathname to derive the active key for the subnav.
 * @param {string} baseUrl - The base url for this release group.
 */
const useActiveKey = (baseUrl: string) => {
  const { pathname } = useLocation();
  const [, activeKey] = pathname.split(baseUrl);

  //  Settings has nested routes, so we need to handle it differently.
  if (activeKey?.startsWith('/settings')) {
    return 'settings';
  }

  if (activeKey?.startsWith('/')) {
    return activeKey.slice(1);
  }

  return activeKey || 'summary';
};

/**
 * Small hook for permission checking for settings tab.
 *
 * @param {number} projectGroupId - The project group id.
 */
const useShouldShowReleaseSettings = (projectGroupId: number): boolean => useMemo(() => {
  const canEditGroup = userHasPermissionForReleaseGroup(Action.Edit, projectGroupId);
  //  Early return if user can edit the group. This allows us to bypass the other checks.
  if (canEditGroup) {
    return true;
  }

  const canEditLicensingPolicy = userHasPermissionToSetReleaseGroupPolicy(projectGroupId, PolicyType.LICENSING);
  if (canEditLicensingPolicy) {
    return true;
  }
  const canEditSecurityPolicy = userHasPermissionToSetReleaseGroupPolicy(projectGroupId, PolicyType.SECURITY);
  if (canEditSecurityPolicy) {
    return true;
  }
  const canEditQualityPolicy = userHasPermissionToSetReleaseGroupPolicy(projectGroupId, PolicyType.QUALITY);
  if (canEditQualityPolicy) {
    return true;
  }
  return false;
}, [projectGroupId]);

type Props = {
  projectGroupId: number;
  projectCount: number;
  licenseCount: number;
  releasesCount: number;
  isLoadingSummary: boolean;
  currentReleaseId?: number;
};

/**
 * ReleaseGroupSubnav is the Subnav for a release group.
 */
const ReleaseGroupSubnav: VoidFunctionComponent<Props> = ({
  projectGroupId,
  projectCount,
  licenseCount,
  releasesCount,
  isLoadingSummary,
  currentReleaseId,
}) => {
  const baseUrl = `/projects/group/${projectGroupId}`;
  const activeKey = useActiveKey(baseUrl);
  const shouldShowSettingsTab = useShouldShowReleaseSettings(projectGroupId);

  const projectGroupReleaseId = useSelectedReleaseId(projectGroupId);
  const scope = useMemo<InventoryScope>(() => ({
    scope: 'releaseGroup',
    projectGroupId,
    //  The inventory counts aren't fetched for a release id of 0.
    projectGroupReleaseId: projectGroupReleaseId ?? 0,
  }), [projectGroupId, projectGroupReleaseId]);

  return (
    <Subnav activeKey={activeKey || 'summary'}>
      <Subnav.Item
        to={baseUrl}
        navKey="summary"
      >
        Summary
      </Subnav.Item>
      <Subnav.ItemWithCount
        to={`${baseUrl}/projects`}
        navKey="projects"
        count={projectCount}
        isLoading={isLoadingSummary}
      >
        Projects
      </Subnav.ItemWithCount>
      {/* A release group with no releases has no issues to count. */}
      {currentReleaseId !== undefined && (
        <ReleaseGroupSubnavItemIssues
          //  TODO: we need to change this type everywhere to always treat projectGroupId as the number that it is.
          projectGroupId={projectGroupId as any as string}
          releaseId={currentReleaseId}
        />
      )}
      <ReleaseGroupSubnavItemInventory
        baseUrl={baseUrl}
        scope={scope}
        //  This helps ensure that we hold off loading the counts until a release group release is selected.
        isLoading={!projectGroupReleaseId}
      />
      <Subnav.ItemWithCount
        to={`${baseUrl}/browse/licenses`}
        navKey="browse/licenses"
        count={licenseCount}
        isLoading={isLoadingSummary}
      >
        Licenses
      </Subnav.ItemWithCount>
      <ReleaseGroupSubnavItemReports
        baseUrl={baseUrl}
        releaseId={currentReleaseId}
      />
      <Subnav.ItemWithCount
        to={`${baseUrl}/releases`}
        navKey="releases"
        count={releasesCount}
        isLoading={isLoadingSummary}
      >
        Releases
      </Subnav.ItemWithCount>
      {shouldShowSettingsTab && (
        <Subnav.Item
          to={`${baseUrl}/settings`}
          navKey="settings"
        >
          Settings
        </Subnav.Item>
      )}
    </Subnav>
  );
};

export default ReleaseGroupSubnav;
